import type { ReactNode, UIEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useModalContext } from '../contexts/ModalContext';
import { cn } from '@/ui/lib/utils';
import { useHasScroll } from '@/ui/hooks/useHasScroll';
import './modalBody.css';

interface ModalBodyProps {
    children: ReactNode;
    className?: string;
}

export const ModalBody = ({ children, className }: ModalBodyProps) => {
    const [isAtBottom, setIsAtBottom] = useState(false);
    const modalBodyRef = useRef<HTMLDivElement>(null);
    const hasScroll = useHasScroll(modalBodyRef);
    const { isFullscreen, setIsFullscreen } = useModalContext();

    const handleScroll = useCallback(
        (e: UIEvent<HTMLDivElement>) => {
            const element = e.currentTarget;

            setIsFullscreen((prev) => {
                if (prev) {
                    return element.scrollHeight === element.clientHeight || element.scrollTop > 0;
                }
                return element.scrollTop >= 150;
            });

            setIsAtBottom(
                Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) < 2,
            );
        },
        [setIsFullscreen],
    );

    useEffect(() => {
        return () => {
            setIsFullscreen(false);
        };
    }, [setIsFullscreen]);

    return (
        <div ref={modalBodyRef} className={cn('modal-body', className)} onScroll={handleScroll}>
            <div
                className={cn(
                    'modal-body__content',
                    isFullscreen && 'modal-body__content--full',
                    className,
                )}
            >
                {children}
            </div>
            <div
                className={cn(
                    'modal-body__fade',
                    (isAtBottom || !hasScroll) && 'modal-body__fade--hidden',
                )}
            />
        </div>
    );
};
